import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Clock, ArrowUpRight, MapPinned } from "lucide-react";
import { studioInfo, hours } from "../../mock";
import { getConsent } from "./CookieConsent";
import SectionLabel from "./SectionLabel";
import Reveal from "./Reveal";

const Location = () => {
  const [mapAllowed, setMapAllowed] = useState(getConsent() === "accepted");

  useEffect(() => {
    const onChange = () => setMapAllowed(getConsent() === "accepted");
    window.addEventListener("onevo-cookie-change", onChange);
    return () => window.removeEventListener("onevo-cookie-change", onChange);
  }, []);

  return (
    <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
      <SectionLabel>Dove siamo</SectionLabel>

      <Reveal>
        <h2 className="font-display text-4xl md:text-7xl text-white mt-8">
          Nel cuore di{" "}
          <span style={{ fontStyle: "italic", fontWeight: 800 }} className="text-white/45">
            Torino.
          </span>
        </h2>
        <p className="text-white/65 text-base md:text-lg leading-relaxed mt-8 max-w-[440px]">
          Raggiungici facilmente in Via Pastrengo 15. Siamo aperti dal mattino presto per
          adattarci ai tuoi ritmi, non ai nostri.
        </p>
      </Reveal>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6 mt-14 items-stretch">
        <Reveal>
          <div className="border-t border-l border-white/10 h-full">
            <div className="border-b border-r border-white/10 p-7 flex items-start gap-4">
              <MapPin size={20} className="text-white/60 mt-1" strokeWidth={1.5} />
              <div>
                <div className="font-mono-label text-[10px] text-white/45 mb-2">Indirizzo</div>
                <div className="text-white text-lg">{studioInfo.address}</div>
                <div className="text-white/55">{studioInfo.city}</div>
              </div>
            </div>
            <a
              href={`tel:${studioInfo.phoneHref}`}
              className="border-b border-r border-white/10 p-7 flex items-start gap-4 hover:bg-white/[0.03] transition-colors duration-300"
            >
              <Phone size={20} className="text-white/60 mt-1" strokeWidth={1.5} />
              <div>
                <div className="font-mono-label text-[10px] text-white/45 mb-2">Telefono</div>
                <div className="text-white text-lg">{studioInfo.phone}</div>
              </div>
            </a>
            <div className="border-b border-r border-white/10 p-7 flex items-start gap-4">
              <Clock size={20} className="text-white/60 mt-1" strokeWidth={1.5} />
              <div className="flex-1">
                <div className="font-mono-label text-[10px] text-white/45 mb-3">Orari</div>
                {hours.map((h) => (
                  <div key={h.days} className="flex justify-between gap-8 text-white/80 py-1">
                    <span>{h.days}</span>
                    <span className="text-white">{h.time}</span>
                  </div>
                ))}
              </div>
            </div>
            <a
              href={studioInfo.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-between border-b border-r border-white/10 p-7 hover:bg-white/[0.03] transition-colors duration-300"
            >
              <span className="font-mono-label text-[11px] text-white">Apri in Google Maps</span>
              <ArrowUpRight size={18} className="text-white/60 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-white" />
            </a>
          </div>
        </Reveal>

        <Reveal delay={120}>
          <div className="img-zoom rounded-sm border border-white/10 h-full min-h-[420px] overflow-hidden">
            {mapAllowed ? (
              <iframe
                title="Mappa ONEVO Via Pastrengo 15 Torino"
                src="https://www.google.com/maps?q=Via+Pastrengo+15,+10128+Torino+TO&output=embed"
                className="w-full h-full min-h-[420px]"
                style={{ border: 0, filter: "grayscale(1) invert(0.92) contrast(0.9)" }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="w-full h-full min-h-[420px] flex flex-col items-center justify-center text-center gap-4 px-8 bg-white/[0.02]">
                <MapPinned size={34} className="text-white/40" strokeWidth={1.4} />
                <p className="text-white/55 text-sm leading-relaxed max-w-[300px]">
                  La mappa di Google richiede il consenso ai cookie di terze parti per essere
                  caricata.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 mt-1">
                  <button
                    onClick={() => {
                      localStorage.setItem("onevo_cookie_consent", "accepted");
                      window.dispatchEvent(new Event("onevo-cookie-change"));
                    }}
                    className="font-mono-label text-[11px] bg-white text-black px-6 py-3 rounded-md hover:bg-white/85 transition-colors"
                  >
                    Accetta e mostra la mappa
                  </button>
                  <a
                    href={studioInfo.mapsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-mono-label text-[11px] text-white border border-white/25 px-6 py-3 rounded-md hover:bg-white/5 transition-colors inline-flex items-center justify-center gap-2"
                  >
                    Apri in Google Maps <ArrowUpRight size={14} />
                  </a>
                </div>
                <Link to="/privacy" className="font-mono-label text-[9px] text-white/40 hover:text-white/70 transition-colors mt-1">
                  Privacy & Cookie Policy
                </Link>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Location;
